import {
  mapGender,
  resolveBirthCityId,
  handleOwnershipStatusValue,
} from "../../../utils/helpers";

// Designation is stored as its master code (genDesignation); the API formData
// expects the full name (genDesgDesc). Accepts either the code or the name,
// and falls back to the raw value when it isn't in the loaded list.
export const getMasterOptionName = (
  rawValue: any,
  options: Array<{ value: any; label: any }> = [],
): string => {
  const code =
    rawValue && typeof rawValue === "object" ? rawValue.value ?? rawValue.label : rawValue;
  if (code === undefined || code === null || code === "") return "";
  const match = options.find(
    (option) => String(option.value) === String(code) || option.label === String(code),
  );
  return match?.label ?? String(code);
};

// Reverse lookup for id payloads: saved data reloads designation as its full
// name, but designationId must stay the code.
const getMasterOptionCode = (
  rawValue: any,
  options: Array<{ value: any; label: any }> = [],
): any => options.find((option) => option.label === rawValue)?.value ?? rawValue;

// Builds the `idDto` (resolved-ID mirror of the raw form values) sent
// alongside the raw formData in every "api/v1/applicant/save-coapplicant"
// call — our own DB, not the processio/LOS calls each caller makes
// separately with their own payloads. Ported from the DIY side's
// src/components/ui/StepperDyanmicForm/helperDto.ts — same shape, kept
// separate per journey since the two component trees don't share files.
//
// `getField(prefix, suffix)` is supplied by the caller because each caller
// resolves a co-applicant field name its own way — this function stays
// oblivious to that and just asks for whichever raw value it needs, by
// section ("basic" | "permanent" | "current" | "employment" | "company")
// and suffix (e.g. "Gender", "OwnershipStatus", "Country").
//
// Every id here comes from data already synchronously available to the
// caller (dropdown master lists, and the coApplicantZipCodeData
// pincode-lookup cache) — this makes no new API calls itself, and nothing
// is reordered to wait on one. If a section's zip cache hasn't been warmed
// yet this session (e.g. Employment was never opened), that section's
// cityId/pincodeId/stateId simply come back blank — same limitation the
// rest of the save-coapplicant payload already has.
export type CoApplicantIdDtoFieldSection =
  | "basic"
  | "permanent"
  | "current"
  | "employment"
  | "company";

// Which tab is actually being saved right now — only that tab's section is
// filled, the rest come back as {}. This isn't cumulative: e.g. saving the
// Address tab fills only addressDetails, not basicDetails too, because a
// tab-scoped save may not even have the other tabs' fields in `values` to
// resolve ids from — sending them anyway would just be stale/blank-but-present
// data. "all" is for callers that save every section in one shot and need
// every section filled at once.
export type CoApplicantIdDtoStage = "basic" | "address" | "employment" | "all";

export interface BuildCoApplicantSaveIdDtoParams {
  getField: (section: CoApplicantIdDtoFieldSection, suffix: string) => any;
  stage: CoApplicantIdDtoStage;
  countryOptions: any[];
  maritalStatusOptions: any[];
  nationalityOptions: any[];
  // Whatever birth-city option list the caller already has synchronously —
  // e.g. a getCityOptionsForCountry(...) result or cached component state.
  // Not fetched here; if it's empty, birthCityId just comes back blank.
  cityOptionsForBirth: any[];
  coApplicantZipCodeData: any;
  coAppKey: string;
  designations?: Array<{ value: any; label: any }>;
}

export const buildCoApplicantSaveIdDto = ({
  getField,
  stage,
  countryOptions,
  maritalStatusOptions,
  nationalityOptions,
  cityOptionsForBirth,
  coApplicantZipCodeData,
  coAppKey,
  designations,
}: BuildCoApplicantSaveIdDtoParams) => {
  const includeBasic = stage === "basic" || stage === "all";
  const includeAddress = stage === "address" || stage === "all";
  const includeEmployment = stage === "employment" || stage === "all";

  // A saved (untouched) field can hold the option's code, its label, or the
  // master row's numeric id (kept as nationalityId / countryId on the option),
  // in any case, so match on all of them.
  const normalizeKey = (value: any) => String(value ?? "").trim().toLowerCase();
  // Every key an option (form JSON or master list) can carry its code, id or
  // name under
  const getOptionKeys = (item: any) =>
    [
      item?.value,
      item?.label,
      item?.code,
      item?.id,
      item?.name,
      item?.desc,
      item?.countryId,
      item?.countryDesc,
      item?.nationalityId,
      item?.nationalityCode,
      item?.nationalityDesc,
      item?.maritalStatusCode,
      item?.maritalStatusName,
      item?.maritalStatusDesc,
    ]
      .map(normalizeKey)
      .filter(Boolean);
  const resolveOption = (raw: any, options: any[]): any => {
    const rawKeys =
      raw && typeof raw === "object"
        ? getOptionKeys(raw)
        : [normalizeKey(raw)].filter(Boolean);
    if (!rawKeys.length) return null;
    return (
      (options || []).find((item: any) =>
        getOptionKeys(item).some((key) => rawKeys.includes(key)),
      ) || null
    );
  };
  // Country options differ by source: dropdownData.countries has the code in
  // `value`, the master list has the name in `value` and the code in `countryId`.
  const getCountryCode = (option: any) =>
    String(option?.countryId || option?.code || option?.value || "");
  // Dropdown fields can hold a selected option object instead of the plain value
  const getPlainValue = (raw: any): string =>
    String(
      (raw && typeof raw === "object" ? raw?.value ?? raw?.label : raw) ?? "",
    ).trim();

  let basicDetails: any = {};
  if (includeBasic) {
    const birthCountry = resolveOption(
      getField("basic", "CountryOfBirth"),
      countryOptions,
    );

    basicDetails = {
      birthCityId: resolveBirthCityId(
        getField("basic", "CityOfBirth"),
        cityOptionsForBirth,
      ),
      birthCountryId: getCountryCode(birthCountry),
      // Saved gender can be the name ("Male"), the code ("M") or an option object
      genderId: (() => {
        const rawGender = getField("basic", "Gender");
        const genderKeys = (
          rawGender && typeof rawGender === "object"
            ? [rawGender?.value, rawGender?.label]
            : [rawGender]
        )
          .map(normalizeKey)
          .filter(Boolean);
        return (
          mapGender().find((item: any) =>
            genderKeys.some(
              (key) =>
                key === normalizeKey(item?.name) || key === normalizeKey(item?.code),
            ),
          )?.code || ""
        );
      })(),
      // The code, not the master row's numeric id (nationalityId)
      maritalStatusId: (() => {
        const option = resolveOption(
          getField("basic", "MaritalStatus"),
          maritalStatusOptions,
        );
        return String(option?.maritalStatusCode ?? option?.code ?? option?.value ?? "");
      })(),
      nationalityId: (() => {
        const option = resolveOption(
          getField("basic", "Nationality"),
          nationalityOptions,
        );
        return String(option?.nationalityCode ?? option?.code ?? option?.value ?? "");
      })(),
      relationshipId: getPlainValue(getField("basic", "RelationWithStudent")),
      salutationId: getPlainValue(getField("basic", "salutation")),
    };
  }

  let addressDetails: any = {};
  if (includeAddress) {
    const permanentCountry = resolveOption(
      getField("permanent", "Country"),
      countryOptions,
    );
    const currentCountry = resolveOption(
      getField("current", "Country"),
      countryOptions,
    );
    const zipData = coApplicantZipCodeData?.[coAppKey] || {};

    addressDetails = {
      permanentAddress: {
        addressTypeId: "PER",
        cityId: zipData?.permanent?.city || "",
        countryId: getCountryCode(permanentCountry),
        ownershipStatusValue: handleOwnershipStatusValue(
          String(getField("permanent", "OwnershipStatus") || ""),
        ),
        pincodeId: zipData?.permanent?.pinCodeId || "",
        stateId: zipData?.permanent?.stateCode || "",
      },
      currentAddress: {
        addressTypeId: "CURRES",
        cityId: zipData?.current?.city || "",
        countryId: getCountryCode(currentCountry),
        ownershipStatusValue: handleOwnershipStatusValue(
          String(getField("current", "OwnershipStatus") || ""),
        ),
        pincodeId: zipData?.current?.pinCodeId || "",
        stateId: zipData?.current?.stateCode || "",
      },
    };
  }

  let employmentDetails: any = {};
  if (includeEmployment) {
    const companyCountry = resolveOption(
      getField("company", "Country"),
      countryOptions,
    );
    const employmentType = getPlainValue(getField("employment", "employmentType"));
    let companyAddressTypeId = "";
    if (employmentType === "Salaried") {
      companyAddressTypeId = "OFFICE";
    } else if (employmentType === "SEP" || employmentType === "SENP") {
      companyAddressTypeId = "BUS";
    }
    const zipData = coApplicantZipCodeData?.[coAppKey] || {};

    // Flat ids (no nested company address); every key is always sent,
    // blank when there's no value.
    employmentDetails = {
      designationId: getPlainValue(
        getMasterOptionCode(getField("employment", "designation"), designations),
      ),
      employmentTypeId:
        employmentType === "Others" ? "OTHER" : employmentType || "",
      addressTypeId: companyAddressTypeId,
      cityId: zipData?.company?.city || "",
      countryId: getCountryCode(companyCountry),
      pincodeId: zipData?.company?.pinCodeId || "",
      stateId: zipData?.company?.stateCode || "",
    };
  }

  return { basicDetails, addressDetails, employmentDetails };
};

export interface BuildApplicantIdDtosBySectionParams {
  values: any;
  countryOptions: any[];
  currentCodeData: any;
  permanentCodeData: any;
  companyCodeData: any;
  entranceExamTypes?: any[];
  languageExamTypes?: any[];
  designations?: Array<{ value: any; label: any }>;
}

// Builds the master-data `idDtos` sent with the applicant create payload,
// keyed by the applicant section being saved (applicantBasic,
// applicantAddress, ...). Same shape as the DIY side's helperDto.
export const buildApplicantIdDtosBySection = ({
  values,
  countryOptions,
  currentCodeData,
  permanentCodeData,
  companyCodeData,
  entranceExamTypes,
  languageExamTypes,
  designations,
}: BuildApplicantIdDtosBySectionParams): Record<string, any> => {
  // Ids are the same ones the create api already sends in `data`
  // (course<N>TypeID, coreExamID, languageExamID — set when the dropdown
  // is picked), falling back to the same lookups used to fill them.
  const ACADEMIC_EXAM_TYPE_IDS: Record<string, number> = {
    "10th": 283,
    "12th": 284,
    Diploma: 285,
    Graduation: 286,
  };
  const academicDetails = Object.keys(values || {})
    .map((key) => key.match(/^course(\d+)Type$/)?.[1])
    .filter((idx): idx is string => !!idx)
    .map((idx) => {
      const examType = values[`course${idx}Type`] || "";
      const examTypeId =
        values[`course${idx}TypeID`] ||
        values[`course${idx}TypeId`] ||
        ACADEMIC_EXAM_TYPE_IDS[examType] ||
        "";
      return { examTypeId: String(examTypeId), examType };
    })
    .filter((item) => item.examTypeId !== "");

  // Empty unless that exam was marked as applied, same as the LOS payload.
  const buildExamIds = (
    applied: any,
    namePrefix: string,
    examTypes: any[] | undefined,
    nameKey: string,
    typeKey: string
  ) =>
    applied === "Yes"
      ? Object.keys(values || {})
        .map((key) => key.match(new RegExp(`^${namePrefix}Name(_\\d+)?$`)))
        .filter((match): match is RegExpMatchArray => !!match)
        // A deleted exam row keeps its keys with blank values (see
        // EntranceExamDetailsSection's removeExamFields) — skip those.
        .filter(([key]) => String(values[key] ?? "").trim() !== "")
        .map(([key, suffix = ""]) => ({
          [nameKey]: values[key] || "",
          [typeKey]:
            values[`${namePrefix}ID${suffix}`] ||
            values[`${namePrefix}Id${suffix}`] ||
            examTypes?.find((elem: any) => elem?.label == values[key])?.id ||
            "",
        }))
      : [];
  const entrance = buildExamIds(
    values?.coreExamApplied,
    "coreExam",
    entranceExamTypes,
    "entranceExam",
    "entranceExamType"
  );
  const language = buildExamIds(
    values?.languageExamApplied,
    "languageExam",
    languageExamTypes,
    "languageExam",
    "entranceLanguageExamType"
  );

  const getCountryId = (country: any) => {
    const label = typeof country === "object" ? country?.label : country;
    return (
      countryOptions?.find((c: any) => c.label === label)?.value || "IN"
    );
  };

  const isEmploymentTypeOthers = values?.employmentType === "Others";
  let companyAddressTypeId = "";
  if (!isEmploymentTypeOthers && values?.employmentType == "Salaried") {
    companyAddressTypeId = "OFFICE";
  } else if (
    !isEmploymentTypeOthers &&
    (values?.employmentType == "SEP" || values?.employmentType == "SENP")
  ) {
    companyAddressTypeId = "BUS";
  }
  // Flat ids (no nested company address); every key is always sent,
  // blank when there's no value (student / Others).
  const emptyEmploymentIds = {
    designationId: "",
    employmentTypeId: "",
    addressTypeId: "",
    cityId: "",
    countryId: "",
    pincodeId: "",
    stateId: "",
  };
  const employmentDetails =
    values?.employmentisEmployed === "Yes"
      ? isEmploymentTypeOthers
        ? { ...emptyEmploymentIds, employmentTypeId: "OTHER" }
        : {
          designationId:
            getMasterOptionCode(values?.employmentDesignation, designations) || "",
          employmentTypeId:
            values?.employmentTypeId === "Others"
              ? "OTHER"
              : values?.employmentTypeId || values?.employmentType || "",
          addressTypeId: companyAddressTypeId,
          cityId: companyCodeData?.city || "",
          // Blank (not the "IN" fallback) when no company country is picked.
          countryId: values?.employmentCompanyCountry
            ? getCountryId(values?.employmentCompanyCountry)
            : "",
          pincodeId: companyCodeData?.pinCodeId || "",
          stateId: companyCodeData?.stateCode || "",
        }
      : { ...emptyEmploymentIds, employmentTypeId: "student" };

  return {
    applicantBasic: {
      basicDetails: {
        genderId:
          mapGender()?.find(
            (item: any) =>
              item.name?.toLowerCase() ===
              String(values?.gender || "").toLowerCase()
          )?.code || "",
        // Same fixed relationship the LOS basic-details call sends for the main applicant.
        relationshipId: "CHILDREN",
        salutationId: values?.salutation || "",
      },
    },
    applicantAddress: {
      addressDetails: {
        currentAddress: {
          cityId: currentCodeData?.city || "",
          countryId: getCountryId(values?.currentCountry),
          addressTypeId: "CURRES",
          ownershipStatusValue: handleOwnershipStatusValue(
            values?.currentOwnershipStatus
          ),
          pincodeId: currentCodeData?.pinCodeId || "",
          stateId: currentCodeData?.stateCode || "",
        },
        permanentAddress: {
          cityId: permanentCodeData?.city || "",
          countryId: getCountryId(values?.permanentCountry),
          addressTypeId: "PER",
          ownershipStatusValue: handleOwnershipStatusValue(
            values?.permanentOwnershipStatus
          ),
          pincodeId: permanentCodeData?.pinCodeId || "",
          stateId: permanentCodeData?.stateCode || "",
        },
      },
    },
    applicantEmployment: { employmentDetails },
    applicantAcademic: { academicDetails },
    applicantEntranceExam: { entranceExamDetails: { entrance, language } },
  };
};

export const APPLICANT_SECTION_BY_VERTICAL_INDEX: Record<number, string> = {
  0: "applicantBasic",
  1: "applicantAddress",
  2: "applicantEmployment",
  3: "applicantAcademic",
  4: "applicantEntranceExam",
};

